import type { Component } from 'vue'
import {
  Activity as ActivityIcon,
  Anchor as AnchorIcon,
  Archive as ArchiveIcon,
  BookOpen as BookOpenIcon,
  Building as BuildingIcon,
  Building2 as Building2Icon,
  Caravan as CaravanIcon,
  ClipboardCheck as ClipboardCheckIcon,
  Clock as ClockIcon,
  Container as ContainerIcon,
  CreditCard as CreditCardIcon,
  DoorOpen as DoorOpenIcon,
  Droplets as DropletsIcon,
  FileText as FileTextIcon,
  Handshake as HandshakeIcon,
  FolderSync as FolderSyncIcon,
  IdCard as IdCardIcon,
  LayoutGrid as LayoutGridIcon,
  List as ListIcon,
  Map as MapIcon,
  Power as PowerIcon,
  Printer as PrinterIcon,
  ReceiptText as ReceiptTextIcon,
  ScanBarcode as ScanBarcodeIcon,
  ShieldUser as ShieldUserIcon,
  Tag as TagIcon,
  Snowflake as SnowflakeIcon,
  Thermometer as ThermometerIcon,
  TriangleAlert as TriangleAlertIcon,
  Truck as TruckIcon,
  Users as UsersIcon,
  Warehouse as WarehouseIcon,
} from 'lucide-vue-next'

export type NavItem = {
  key: string
  label: string
  path?: string
  /** A lucide icon, kept with the site: the menu draws with no internet (task 2303). */
  icon?: Component
  requiresAll?: string[]
  requiresAny?: string[]
  children?: NavItem[]
}

export type NavSection = {
  key: string
  title: string
  items: NavItem[]
}

function normalizePermissions(values: string[] | undefined): string[] {
  if (!Array.isArray(values)) return []
  return values
    .map(value => (typeof value === 'string' ? value.trim() : ''))
    .filter(Boolean)
}

export function canAccessNavItem(
  item: NavItem,
  permissions: Set<string>,
  isAdmin = false
): boolean {
  if (isAdmin) return true

  const requiresAll = normalizePermissions(item.requiresAll)
  const requiresAny = normalizePermissions(item.requiresAny)

  const hasAll = requiresAll.length === 0
    ? true
    : requiresAll.every(permission => permissions.has(permission))
  const hasAny = requiresAny.length === 0
    ? true
    : requiresAny.some(permission => permissions.has(permission))

  return hasAll && hasAny
}

export function filterNav(
  items: NavItem[],
  permissions: Set<string>,
  isAdmin = false
): NavItem[] {
  const visible: NavItem[] = []

  for (const item of items ?? []) {
    const filteredChildren = item.children
      ? filterNav(item.children, permissions, isAdmin)
      : []
    const hasVisibleChildren = filteredChildren.length > 0
    const canAccessSelf = canAccessNavItem(item, permissions, isAdmin)
    const hasPath = Boolean(item.path)

    if (!canAccessSelf && !hasVisibleChildren) {
      continue
    }

    if (!hasPath && !hasVisibleChildren) {
      continue
    }

    const nextItem: NavItem = { ...item }

    if (!canAccessSelf) {
      delete nextItem.path
    }

    if (hasVisibleChildren) {
      nextItem.children = filteredChildren
    } else {
      delete nextItem.children
    }

    visible.push(nextItem)
  }

  return visible
}

export function filterNavSections(
  sections: NavSection[],
  permissions: Set<string>,
  isAdmin = false
): NavSection[] {
  const visible: NavSection[] = []

  for (const section of sections ?? []) {
    const items = filterNav(section.items, permissions, isAdmin)
    if (items.length === 0) {
      continue
    }

    visible.push({
      ...section,
      items
    })
  }

  return visible
}

// Permission keys are `<Area>.<Level>` (task 2318): View, Edit, Manage, each including the levels
// below it, so a screen is visible to anyone who can View its area. Each role sees only the screens
// of its job (task 2321, "Who sees what"). Every page has its own area (Mark, 2026-10-26), its
// setup that area's Manage; tests/layout/roles.layout.spec.ts holds each role's menu. The Admin
// role bypasses every check at the auth-store layer.
function view(...areas: string[]): string[] {
  return [...new Set(areas)].map(area => `${area}.View`)
}


export const mainNavSections: NavSection[] = [
  {
    title: 'Warehouse',
    key: 'warehouse',
    // A group's dashboard is a tab on Home, never a menu link (navigation/homeTabs.ts).
    // The order is Mark's (2026-10-19): Driver Arrival, Dock Operations, Forecast, Truck Inspections,
    // Orders, Cross Dock Payments, Truck Services, Trucking Companies, Warehouses, Door Board,
    // Room Climate.
    items: [
      {
        key: 'warehouse-driver-arrival',
        label: 'Driver Arrival',
        icon: TruckIcon,
        path: '/warehouse/driver-arrival',
        requiresAny: view('DriverArrival')
      },
      {
        key: 'warehouse-dock-operations',
        label: 'Dock Operations',
        icon: Building2Icon,
        path: '/warehouse/dock-operations',
        requiresAny: view('DockOperations')
      },
      {
        key: 'warehouse-forecast',
        label: 'Forecast',
        icon: ArchiveIcon,
        path: '/warehouse/forecast',
        requiresAny: view('Forecasts')
      },
      // Truck inspections and their photos, the proof to show if ever asked (task 2215): a page
      // of its own (Mark, 2026-10-26). Under Dock Operations, whose inspections it shows (Mark,
      // 2026-10-19).
      {
        key: 'warehouse-truck-inspections',
        label: 'Truck Inspections',
        icon: ClipboardCheckIcon,
        path: '/warehouse/truck-inspections',
        requiresAny: view('TruckInspections')
      },
      {
        key: 'warehouse-orders',
        label: 'Orders',
        icon: ReceiptTextIcon,
        path: '/warehouse/orders',
        requiresAny: view('Orders')
      },
      // Commercial cross docks paid through Square (task 2501): waiting, paid, overridden, refunded.
      {
        key: 'warehouse-cross-dock-payments',
        label: 'Cross Dock Payments',
        icon: CreditCardIcon,
        path: '/warehouse/cross-dock-payments',
        requiresAny: view('CrossDocks')
      },
      // Truck Services (Mark, 2026-10-19; Truck Washouts before): washouts, repairs, escorts and
      // waiting time for a truck, paid by the driver through a link texted to them. Their own area:
      // View sees them, Edit works one, Manage refunds a line.
      {
        key: 'warehouse-truck-services',
        label: 'Truck Services',
        icon: DropletsIcon,
        path: '/warehouse/truck-services',
        requiresAny: view('TruckServices')
      },
      // Trucking Companies is the shared hauler master, a page of its own. The TIR typeahead still
      // finds and adds a company as part of writing a receipt. Under Truck Services, whose
      // windows pick from it (Mark, 2026-10-19; Yard before).
      {
        key: 'warehouse-trucking-companies',
        label: 'Trucking Companies',
        icon: TruckIcon,
        path: '/warehouse/trucking-companies',
        requiresAny: view('TruckingCompanies')
      },
      // Warehouses, a page of its own. A warehouse opens to its own page, which holds its doors,
      // gangs, rooms and inventory locations as tabs: set-up lives on the screen it serves
      // (app/components/_CONVENTIONS.md), not in a menu entry each.
      {
        key: 'warehouse-settings-warehouses',
        label: 'Warehouses',
        icon: WarehouseIcon,
        path: '/warehouse/warehouses',
        requiresAny: view('Warehouses')
      },
      // The Door Board (task 2539): every warehouse door and what is at it, for the clerks and a TV.
      // Under Warehouses, whose doors it shows (Mark, 2026-10-19).
      {
        key: 'warehouse-door-board',
        label: 'Door Board',
        icon: DoorOpenIcon,
        path: '/warehouse/door-board',
        requiresAny: view('DoorBoard')
      },
      {
        key: 'warehouse-room-climate',
        label: 'Room Climate',
        icon: ThermometerIcon,
        path: '/warehouse/room-climate',
        requiresAny: view('RoomClimate')
      }
    ]
  },
  {
    title: 'Inventory',
    key: 'inventory',
    items: [
      // One Inventory screen (task 2317): By Product, By Pallet and By Container are its tabs.
      // The Inventory area (InventorySupply) opens all three to look at (task 2321); Containers
      // alone does not show it, so a role given Containers for the yard is not handed Inventory.
      {
        key: 'inventory-stock',
        label: 'Inventory',
        icon: FileTextIcon,
        path: '/inventory',
        requiresAny: view('Inventory')
      },
      {
        key: 'inventory-pallet-incidents',
        label: 'Pallet Incidents',
        icon: TriangleAlertIcon,
        path: '/inventory/pallet-incidents',
        requiresAny: view('PalletIncidents')
      }
    ]
  },
  {
    title: 'Yard',
    key: 'yard',
    items: [
      // Trailer Interchange Receipts — the gate ledger (ID03). The gate is the
      // entry point of the yard flow (an IN enqueues a yard pick); Read-gated so
      // any reader lands on the board, New buttons gate on TrailerInterchange.Edit.
      {
        key: 'gate-receipts',
        label: 'TIR',
        icon: TruckIcon,
        path: '/trailer-interchange-receipts',
        requiresAny: view('TrailerInterchange')
      },
      {
        key: 'yard-picks',
        label: 'Yard Picks',
        icon: ListIcon,
        path: '/yard/yard-picks',
        requiresAny: view('YardPicks')
      },
      // Reefer temperature checks (task 2701): walk the yard, find any container, the overdue list.
      {
        key: 'yard-reefer-checks',
        label: 'Reefer Checks',
        icon: SnowflakeIcon,
        path: '/yard/reefer-checks',
        requiresAny: view('ReeferChecks')
      },
      // Container Mapping (Mark, 2026-10-21): Bay Inventory and Container Movements as one screen,
      // the moves made and their history: a page of its own (Mark, 2026-10-26).
      {
        key: 'yard-bay-inventory',
        label: 'Container Mapping',
        icon: LayoutGridIcon,
        path: '/yard/bay-inventory',
        requiresAny: view('ContainerMapping')
      },
      // Yards (Mark, 2026-10-19): the yard's set-up, its yards, bays, cells and location
      // proximities as tabs, a screen of its own as Warehouses is; Container Mapping's gear opens them too.
      {
        key: 'yard-yards',
        label: 'Yards',
        icon: MapIcon,
        path: '/yard/yards',
        requiresAny: view('Yards')
      },
      // Container master (owner, ISO, tare, seal, port status). Read-gated per the
      // view-only pattern; write actions gate on Containers.Edit/Update/Delete.
      {
        key: 'yard-containers',
        label: 'Containers',
        icon: ContainerIcon,
        path: '/yard/containers',
        requiresAny: view('Containers')
      },
      // Chassis and Gensets, each a page of its own (Mark, 2026-10-26); TIR looks them up at the
      // gate with its own View (decision 0002).
      {
        key: 'yard-chassis',
        label: 'Chassis',
        icon: CaravanIcon,
        path: '/yard/chassis',
        requiresAny: view('Chassis')
      },
      {
        key: 'yard-gensets',
        label: 'Gensets',
        icon: PowerIcon,
        path: '/yard/gensets',
        requiresAny: view('Gensets')
      }
    ]
  },
  {
    title: 'Ship',
    key: 'ship',
    items: [
      {
        key: 'ship-planning',
        label: 'Ship Planning',
        icon: AnchorIcon,
        path: '/ship/ship-planning',
        requiresAny: view('Ships')
      }
    ]
  },
  {
    title: 'Workforce',
    key: 'workforce',
    items: [
      {
        key: 'workforce-rosters',
        label: 'Rosters',
        icon: UsersIcon,
        path: '/workforce/rosters',
        requiresAny: view('Rosters')
      },
      {
        key: 'workforce-employees',
        label: 'Employees',
        icon: IdCardIcon,
        path: '/workforce/employees',
        // The people themselves, a page of its own; Rosters above is the day's look-up.
        requiresAny: view('Employees')
      }
    ]
  },
  {
    title: 'Admin',
    key: 'admin',
    items: [
      // Nothing new goes here: set-up lives on the screen it serves, behind the gear in its header
      // (Room Climate, Pallet Incidents, TIR, Yard Pick, Container Movements) or as a tab of the
      // record that owns it (a warehouse's doors, gangs and rooms). Each entry is a page with its
      // own area (Mark, 2026-10-26). Order: Mark, 2026-10-20.
      // The site's own company record: its name and logo, on printed reports (task 2314). Mark,
      // 2026-10-14: under Admin, since no screen owns it.
      {
        key: 'admin-company',
        label: 'Company',
        icon: BuildingIcon,
        path: '/admin/company',
        requiresAny: view('Company')
      },
      {
        // One Security screen (task 2319): Users, Roles and Service Clients as its tabs.
        key: 'admin-security',
        label: 'Security',
        icon: ShieldUserIcon,
        path: '/admin/security',
        requiresAny: view('Security')
      },
      // The partners, the business only (task 2446, Mark 2026-10-15).
      {
        key: 'admin-partners',
        label: 'Partners',
        icon: HandshakeIcon,
        path: '/admin/settings/partners',
        requiresAny: view('Partners')
      },
      // Everything about partners' files: every document in and out (Data Exchange), and each
      // partner's transfers and connections and our own file server: one page, its own area.
      {
        key: 'admin-data-exchange',
        label: 'Data Exchange',
        icon: FolderSyncIcon,
        path: '/admin/settings/data-exchange',
        requiresAny: view('DataExchange')
      },
      // Tablets: the forklift tablets' own Wi-Fi and motion, their register and the drivers' history
      // (task 1705; Mark, 2026-10-16: under Admin, beside Barcoding Settings; was Tablet Log until
      // 2026-10-17). A page of its own (Mark, 2026-10-26).
      {
        key: 'admin-tablets',
        label: 'Tablets',
        icon: ActivityIcon,
        path: '/admin/tablets',
        requiresAny: view('Tablets')
      },
      // Site-wide settings of the forklift barcoding site, and its Trailer Layouts.
      {
        key: 'admin-barcoding',
        label: 'Barcoding Settings',
        icon: ScanBarcodeIcon,
        path: '/admin/settings/barcoding',
        // Trailer Layouts are a tab here (task 2320).
        requiresAny: view('BarcodingSettings')
      },
      // The site's printers (task 2449), a page of their own beside Tablets since task 2553 (Mark,
      // 2026-10-17; was a tab of Barcoding Settings).
      {
        key: 'admin-printers',
        label: 'Printers',
        icon: PrinterIcon,
        path: '/admin/printers',
        requiresAny: view('Printers')
      },
      {
        key: 'admin-tag-definitions',
        label: 'Tag Definitions',
        icon: TagIcon,
        path: '/admin/settings/tag-definitions',
        requiresAny: view('TagDefinitions')
      },
      // One System screen (task 2433): Jobs, Logs and System Health are its tabs, each on its own permission.
      {
        key: 'admin-system',
        label: 'System',
        icon: ClockIcon,
        path: '/admin/system',
        requiresAny: view('System')
      }
    ]
  }
]

export const bottomNavItems: NavItem[] = [
  {
    key: 'help-docs',
    label: 'Help / Docs',
    icon: BookOpenIcon,
    path: '/help-docs'
  },
]

// ── Partner users (task 1309) ────────────────────────────────────────────────────────────────
// A partner's own people see their own orders, pallets, containers, gate receipts, reefer checks,
// chassis and gensets, and Home with its dashboards counted over their own items (Mark,
// 2026-10-20), and nothing else. The server is what holds this (only endpoints open to
// partners, and only their partners' rows); the menu and the route guard only keep the screens
// from offering what the server refuses. Keep in step with the API's partner-endpoints.txt.

/** The screens a partner user may open: each path and anything under it, except PARTNER_NEVER. */
export const PARTNER_PATHS = [
  '/warehouse/orders',
  '/inventory/by-pallet',
  '/inventory/by-container',
  '/yard/containers',
  '/trailer-interchange-receipts',
  '/yard/reefer-checks',
  '/yard/chassis',
  '/yard/gensets',
  // Their own files in and out: Data Exchange shows them its Documents tab alone (Mark, 2026-10-16).
  '/admin/settings/data-exchange',
  '/help-docs',
  // The old dashboard addresses: each opens its tab on Home.
  '/warehouse/dashboard',
  '/inventory/dashboard',
  '/yard/dashboard',
]

/** Under a partner path, but writing: never a partner's. */
const PARTNER_NEVER = ['/warehouse/orders/new', '/trailer-interchange-receipts/new', '/trailer-interchange-receipts/gate-passes', '/admin/settings/data-exchange/transfers']

export function partnerMayOpen(path: string): boolean {
  const clean = (path || '/').split(/[?#]/)[0]!.replace(/\/+$/, '') || '/'
  if (PARTNER_NEVER.some(p => clean === p || clean.startsWith(`${p}/`))) return false
  // Home and its dashboards (Mark, 2026-10-20): every figure the server sends a partner is their own.
  if (clean === '/') return true
  if (clean === '/inventory') return true // it opens on By Pallet for them
  return PARTNER_PATHS.some(p => clean === p || clean.startsWith(`${p}/`))
}

/**
 * A dashboard figure's link: none for a partner user when it leads to a screen they may not open
 * (the figure stays, as a plain count).
 */
export function dashboardLink(to: string | null | undefined, isPartnerUser: boolean): string | null {
  if (!to) return null
  return isPartnerUser && !partnerMayOpen(to) ? null : to
}

/** The menu a partner user sees: only the entries whose screen they may open; Inventory opens on By Pallet. */
export function partnerNavSections(sections: NavSection[]): NavSection[] {
  const keep = (item: NavItem): NavItem | null => {
    const path = item.key === 'inventory-stock' ? '/inventory/by-pallet' : item.path
    if (path && partnerMayOpen(path)) return { ...item, path, children: undefined }
    return null
  }
  return sections
    .map(section => ({ ...section, items: section.items.map(keep).filter((i): i is NavItem => i !== null) }))
    .filter(section => section.items.length > 0)
}

/**
 * The menu a person sees: the side menu draws it, and Home's screen buttons are made from it
 * (Mark, 2026-10-22), so the two never differ. Partner users get only the partner screens.
 */
export function menuSectionsFor(permissions: Set<string>, isAdmin: boolean, isPartnerUser: boolean): NavSection[] {
  const sections = filterNavSections(mainNavSections, permissions, isAdmin)
  return isPartnerUser ? partnerNavSections(sections) : sections
}

export type ScreenButton = { key: string, label: string, path: string, icon?: Component }
export type ScreenGroup = { key: string, title: string, screens: ScreenButton[] }

/**
 * Every screen a menu opens, by its section, in the menu's order: an entry that opens a screen,
 * or each screen under a group (with the group's icon when it has none of its own).
 */
export function menuScreens(sections: NavSection[]): ScreenGroup[] {
  const leaves = (item: NavItem, icon?: Component): ScreenButton[] =>
    item.children?.length
      ? item.children.flatMap(child => leaves(child, item.icon ?? icon))
      : item.path ? [{ key: item.key, label: item.label, path: item.path, icon: item.icon ?? icon }] : []
  return sections
    .map(section => ({ key: section.key, title: section.title, screens: section.items.flatMap(item => leaves(item)) }))
    .filter(group => group.screens.length > 0)
}
